export type BattlePoint = { x: number; y: number };
export type BattleRect = { x: number; y: number; width: number; height: number; left: number; right: number; top: number; bottom: number; contains(x: number, y: number): boolean };
export type BattleBounds = { left: number; right: number; top: number; bottom: number };

export class BattleNavigation {
  readonly corners: BattlePoint[] = [];

  constructor(private readonly obstacles: BattleRect[], private readonly bounds: BattleBounds, private readonly radius: number) {
    this.buildCorners();
  }

  canStand(x: number, y: number) {
    return x >= this.bounds.left && x <= this.bounds.right && y >= this.bounds.top && y <= this.bounds.bottom && this.lineClear(x, y, x, y, this.radius);
  }

  /**
   * Returns the point a body should actually occupy: the nearest position to
   * `(x, y)` that is inside the arena, clear of every obstacle, and - when an
   * `anchor` is given - able to reach that anchor by walking.
   *
   * A body that is already standing clear is returned untouched. A body that
   * overlaps terrain can only leave by whichever edge is closest, so the nearby
   * clear positions are collected and the closest is taken, because leaving one
   * obstacle can land the body inside a neighbouring one.
   *
   * The `anchor` matters because interlocking terrain can leave a gap that is
   * technically clear but sealed off; a body pushed into such a pocket is clear
   * of the rock yet can never move or attack again, which silently turns a
   * battle into an unavoidable loss. Passing the opponent's position rejects
   * those pockets, since anywhere the opponent cannot be reached from is sealed.
   */
  resolveStanding(x: number, y: number, anchor?: BattlePoint): BattlePoint {
    const px = this.clampX(x), py = this.clampY(y);
    const here = { x: px, y: py };
    const overlapsTerrain = this.obstacles.some((obstacle) => this.overlaps(px, py, obstacle));
    if (!overlapsTerrain && (!anchor || this.canReach(here, anchor))) return here;
    const clear = this.clearPoints(this.candidatePoints(px, py), px, py);
    if (!clear.length) return here;
    if (anchor) {
      // Nearest first, and capped, so the walk-reachability test stays cheap on
      // the rare frames where a body has to be pushed out of terrain.
      for (const point of clear.slice(0, 24)) if (this.canReach(point, anchor)) return point;
    }
    return clear[0]!;
  }

  /** Every position worth trying when a body has to be pushed out of terrain, nearest edge first. */
  private candidatePoints(x: number, y: number): BattlePoint[] {
    // Clearing the padded edge by a pixel is not enough: `segmentHitsRect`
    // treats a point sitting exactly on that edge as a hit.
    const margin = 2, exits: BattlePoint[] = [];
    for (const obstacle of this.obstacles) {
      if (!this.overlaps(x, y, obstacle)) continue;
      exits.push(
        { x: obstacle.left - this.radius - margin, y },
        { x: obstacle.right + this.radius + margin, y },
        { x, y: obstacle.top - this.radius - margin },
        { x, y: obstacle.bottom + this.radius + margin },
      );
    }
    return [...exits, ...this.corners, ...this.gridCandidates()];
  }

  /** Cheap sweep of the arena floor, used to widen the search when the edges fail. */
  private gridCandidates(): BattlePoint[] {
    const step = 8, points: BattlePoint[] = [];
    for (let y = this.bounds.top; y <= this.bounds.bottom; y += step) for (let x = this.bounds.left; x <= this.bounds.right; x += step) points.push({ x, y });
    return points;
  }

  /** The clear candidate positions, nearest to `(x, y)` first and free of duplicates. */
  private clearPoints(candidates: BattlePoint[], x: number, y: number): BattlePoint[] {
    const unique = new Map<string, BattlePoint>();
    for (const candidate of candidates) {
      const point = { x: this.clampX(candidate.x), y: this.clampY(candidate.y) };
      if (!this.canStand(point.x, point.y)) continue;
      unique.set(`${Math.round(point.x)},${Math.round(point.y)}`, point);
    }
    return [...unique.values()].sort((a, b) => this.distance(a, { x, y }) - this.distance(b, { x, y }));
  }

  /** Whether a body standing at `from` could walk to `to` at all. */
  private canReach(from: BattlePoint, to: BattlePoint) {
    if (!this.canStand(to.x, to.y)) return false;
    return this.lineClear(from.x, from.y, to.x, to.y, this.radius) || this.nextWaypoint(from, to) !== undefined;
  }

  private overlaps(x: number, y: number, obstacle: BattleRect) {
    return x >= obstacle.left - this.radius && x <= obstacle.right + this.radius && y >= obstacle.top - this.radius && y <= obstacle.bottom + this.radius;
  }

  private clampX(x: number) { return Math.max(this.bounds.left, Math.min(this.bounds.right, x)); }

  private clampY(y: number) { return Math.max(this.bounds.top, Math.min(this.bounds.bottom, y)); }

  lineClear(x1: number, y1: number, x2: number, y2: number, radius = 0) {
    return !this.obstacles.some((obstacle) => this.segmentHitsRect(x1, y1, x2, y2, obstacle, radius));
  }

  nextWaypoint(start: BattlePoint, goal: BattlePoint): BattlePoint | undefined {
    if (!this.canStand(goal.x, goal.y)) return undefined;
    if (this.lineClear(start.x, start.y, goal.x, goal.y, this.radius)) return goal;
    const points = [start, ...this.corners, goal], count = points.length;
    const distances = Array(count).fill(Number.POSITIVE_INFINITY) as number[];
    const previous = Array(count).fill(-1) as number[];
    const visited = Array(count).fill(false) as boolean[];
    distances[0] = 0;
    for (let iteration = 0; iteration < count; iteration++) {
      let current = -1;
      for (let i = 0; i < count; i++) if (!visited[i] && (current < 0 || distances[i]! < distances[current]!)) current = i;
      if (current < 0 || !Number.isFinite(distances[current]!)) break;
      if (current === count - 1) break;
      visited[current] = true;
      for (let i = 1; i < count; i++) {
        if (i === current || visited[i]) continue;
        const a = points[current]!, b = points[i]!;
        if (!this.lineClear(a.x, a.y, b.x, b.y, this.radius)) continue;
        const next = distances[current]! + this.distance(a, b);
        if (next < distances[i]!) { distances[i] = next; previous[i] = current; }
      }
    }
    if (!Number.isFinite(distances[count - 1]!)) return undefined;
    let first = count - 1;
    while (previous[first]! > 0) first = previous[first]!;
    return points[first];
  }

  segmentHitsRect(x1: number, y1: number, x2: number, y2: number, rect: BattleRect, padding = 0) {
    const left = rect.left - padding, right = rect.right + padding, top = rect.top - padding, bottom = rect.bottom + padding;
    const steps = Math.max(2, Math.ceil(this.distance({ x: x1, y: y1 }, { x: x2, y: y2 }) / 5));
    for (let i = 1; i <= steps; i++) {
      const t = i / steps, x = x1 + (x2 - x1) * t, y = y1 + (y2 - y1) * t;
      if (x >= left && x <= right && y >= top && y <= bottom) return true;
    }
    return false;
  }

  segmentDistanceSq(x1: number, y1: number, x2: number, y2: number, px: number, py: number) {
    const dx = x2 - x1, dy = y2 - y1, length = dx * dx + dy * dy;
    const t = length ? Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / length)) : 0;
    return (px - (x1 + dx * t)) ** 2 + (py - (y1 + dy * t)) ** 2;
  }

  private buildCorners() {
    for (const obstacle of this.obstacles) {
      const candidates: BattlePoint[] = [
        { x: obstacle.left - this.radius - 5, y: obstacle.top - this.radius - 5 },
        { x: obstacle.right + this.radius + 5, y: obstacle.top - this.radius - 5 },
        { x: obstacle.left - this.radius - 5, y: obstacle.bottom + this.radius + 5 },
        { x: obstacle.right + this.radius + 5, y: obstacle.bottom + this.radius + 5 },
      ];
      for (const candidate of candidates) {
        const point = { x: Math.max(this.bounds.left, Math.min(this.bounds.right, candidate.x)), y: Math.max(this.bounds.top, Math.min(this.bounds.bottom, candidate.y)) };
        if (this.canStand(point.x, point.y) && !this.corners.some((other) => this.distance(point, other) < 8)) this.corners.push(point);
      }
    }
  }

  private distance(a: BattlePoint, b: BattlePoint) { return Math.hypot(a.x - b.x, a.y - b.y); }
}
